import clsx from 'clsx'
import { motion } from 'motion/react'
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { type Money } from 'utils/web_api.schemas'

import ChangeIndicator from '../ChangeIndicator/ChangeIndicator'

export interface BudgetComparisonBarProps {
  condensed: boolean;
  beforeLabel: string;
  afterLabel: string;
  beforeAmount: Money;
  afterAmount: Money | null;
  change: Money | null;
  xAxisMaxAmountSubunits: number;
  increaseIsGreen: boolean;
  beforeAmountLabel?: string;
  /** Apply min-width to labels for alignment (e.g., "Before"/"After" need alignment, year numbers don't) */
  useMinLabelWidth?: boolean;
}

interface BarProps {
  label: string;
  isAfter: boolean;
  amount: Money;
  maxAmountSubunits: number;
  percentageChange?: number | null;
  increaseIsGreen?: boolean;
  condensed?: boolean;
  amountIsUnknown?: boolean;
  useMinLabelWidth?: boolean;
}
interface BudgetComparisonBarWithDifferentSignsProps {
  condensed: boolean;
  beforeLabel: string;
  afterLabel: string;
  beforeAmount: Money;
  afterAmount: Money;
  xAxisMaxAmountSubunits: number;
  beforeAmountLabel?: string;
  changeSubunits: number;
  useMinLabelWidth?: boolean;
}

export const Bar = ({
  label,
  isAfter,
  amount,
  maxAmountSubunits,
  percentageChange,
  increaseIsGreen,
  condensed,
  amountIsUnknown,
  useMinLabelWidth,
}: BarProps) => {
  // Memoize width percentage calculation
  const widthPercentage = useMemo(() => {
    if (maxAmountSubunits === 0) return 0
    return Math.abs(amount.subunits / maxAmountSubunits)
  }, [amount.subunits, maxAmountSubunits])

  const outerContainerRef = useRef<HTMLDivElement>(null)
  const fullWidthRef = useRef<HTMLDivElement>(null)
  const labelWidthRef = useRef<HTMLDivElement>(null)
  const timeLabelWidthRef = useRef<HTMLSpanElement>(null)
  const [barWidth, setBarWidth] = useState(0)

  const calculateBarWidth = useCallback(() => {
    if (outerContainerRef.current && labelWidthRef.current) {
      const availableWidth =
        outerContainerRef.current.offsetWidth -
        labelWidthRef.current.offsetWidth -
        (timeLabelWidthRef.current?.offsetWidth || 0) -
        16
      const barWidth = Math.max(0, availableWidth * widthPercentage)
      setBarWidth(barWidth)
    }
  }, [widthPercentage])

  useLayoutEffect(() => {
    calculateBarWidth()

    const resizeObserver = new ResizeObserver(calculateBarWidth)
    if (outerContainerRef.current) {
      resizeObserver.observe(outerContainerRef.current)
    }
    if (timeLabelWidthRef.current) {
      resizeObserver.observe(timeLabelWidthRef.current)
    }
    if (fullWidthRef.current) {
      resizeObserver.observe(fullWidthRef.current)
    }
    if (labelWidthRef.current) {
      resizeObserver.observe(labelWidthRef.current)
    }

    // Throttle window resize for better performance
    let resizeTimeout: ReturnType<typeof setTimeout>
    const throttledResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(calculateBarWidth, 16) // ~60fps
    }

    window.addEventListener('resize', throttledResize)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', throttledResize)
      clearTimeout(resizeTimeout)
    }
  }, [widthPercentage, condensed])

  return (
    <div
      ref={outerContainerRef}
      className='flex flex-row gap-2 items-center w-full'
    >
      {!condensed && (
        <span
          ref={timeLabelWidthRef}
          className={clsx('body text-gray-800', {
            'min-w-12': useMinLabelWidth,
          })}
        >
          {label}
        </span>
      )}
      <div
        className='flex flex-row gap-2 items-center flex-grow'
        ref={fullWidthRef}
      >
        <motion.div
          className={clsx(
            'rounded-full',
            isAfter ? 'bg-thatch-400' : 'bg-gray-300',
            { 'h-3': condensed, 'h-4': !condensed },
            amountIsUnknown
              ? 'bg-white border border-gray-400 border-dashed'
              : ''
          )}
          style={{ width: barWidth }}
          layout='size'
        />
        <div className='flex flex-row gap-2 items-center' ref={labelWidthRef}>
          <span
            className={clsx({
              'body text-gray-500': !isAfter,
              'body-bold text-gray-800': isAfter,
              'body-sm': condensed && !isAfter,
              'body-sm-bold': condensed && isAfter,
            })}
          >
            {amountIsUnknown
              ? 'Unknown'
              : amount.formatted_amount.replace('-', '')}
          </span>
          {!condensed &&
            !!percentageChange &&
            percentageChange !== 0 &&
            !amountIsUnknown && (
              <ChangeIndicator
                change={percentageChange}
                displayValue={`${Math.abs(percentageChange)}%`}
                increaseIsGreen={increaseIsGreen || false}
                makeInvisible={!isAfter}
              />
          )}
        </div>
      </div>
    </div>
  )
}

const BudgetComparisonBarWithDifferentSigns: React.FC<
  BudgetComparisonBarWithDifferentSignsProps
> = ({
  condensed,
  beforeLabel,
  afterLabel,
  beforeAmount,
  afterAmount,
  xAxisMaxAmountSubunits,
  beforeAmountLabel,
  changeSubunits,
  useMinLabelWidth,
}) => {
  const maxAmount = xAxisMaxAmountSubunits + Math.abs(beforeAmount.subunits)
  const leftWidth = Math.abs(beforeAmount.subunits / maxAmount) * 100
  const rightWidth = Math.abs(afterAmount.subunits / maxAmount) * 100

  return (
    <table
      data-testid='budget-comparison-bar-with-different-signs'
      className='table-fixed'
    >
      <tbody>
        <tr>
          {!condensed && (
            <td
              className={clsx('body text-gray-800 py-1 pr-1 whitespace-nowrap', {
                'min-w-12': useMinLabelWidth,
              })}
            >
              {beforeLabel}
            </td>
          )}
          <td
            className='border-r border-gray-200 border-dashed px-1'
            width={`${leftWidth}%`}
          >
            <div
              className={clsx(
                'rounded-full flex-grow w-full bg-gray-300',
                { 'h-3': condensed, 'h-4': !condensed }
              )}
            />
          </td>
          <td className='px-1' colSpan={2}>
            <div className='flex flex-row gap-2 items-center'>
              <span
                className={clsx('text-gray-500', {
                  body: !condensed,
                  'body-sm': condensed,
                })}
              >
                {beforeAmount.formatted_amount}
                {!condensed && beforeAmountLabel && (
                  <span className='caption text-gray-500 ml-1 text-nowrap'>
                    {beforeAmountLabel}
                  </span>
                )}
              </span>
            </div>
          </td>
        </tr>
        <tr>
          {!condensed && (
            <td
              className={clsx('body text-gray-800 py-1 pr-1 whitespace-nowrap', {
                'min-w-12': useMinLabelWidth,
              })}
            >
              {afterLabel}
            </td>
          )}
          <td className='border-r border-gray-200 border-dashed px-1' />
          <td className='px-1' width={`${rightWidth}%`}>
            <div
              className={clsx('rounded-full flex-grow w-full bg-thatch-400', {
                'h-3': condensed,
                'h-4': !condensed,
              })}
            />
          </td>
          <td className='px-1'>
            <div className='flex flex-row gap-2 items-center'>
              <span
                className={clsx('text-gray-800', {
                  'body-bold': !condensed,
                  'body-sm-bold': condensed,
                })}
              >
                {afterAmount.formatted_amount}
              </span>
              {!condensed && (
                <ChangeIndicator
                  change={changeSubunits}
                  displayValue=''
                  increaseIsGreen
                  makeInvisible={false}
                />
              )}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  )
}

const BudgetComparisonBar: React.FC<BudgetComparisonBarProps> = ({
  condensed,
  beforeLabel,
  afterLabel,
  beforeAmount,
  afterAmount,
  change,
  xAxisMaxAmountSubunits,
  increaseIsGreen,
  beforeAmountLabel,
  useMinLabelWidth,
}) => {
  if (
    afterAmount &&
    !!change &&
    beforeAmount.subunits * afterAmount.subunits < 0
  ) {
    return (
      <BudgetComparisonBarWithDifferentSigns
        condensed={condensed}
        beforeLabel={beforeLabel}
        afterLabel={afterLabel}
        beforeAmount={beforeAmount}
        afterAmount={afterAmount}
        xAxisMaxAmountSubunits={xAxisMaxAmountSubunits}
        beforeAmountLabel={beforeAmountLabel}
        changeSubunits={change.subunits}
        useMinLabelWidth={useMinLabelWidth}
      />
    )
  }

  // Memoize percentage calculation
  const percentageChange = useMemo(() => {
    if (condensed || !change) return null

    if (beforeAmount.subunits === 0 || change.subunits === 0) {
      return 0
    }

    return Math.round((change.subunits / beforeAmount.subunits) * 100)
  }, [condensed, beforeAmount.subunits, change?.subunits])

  return (
    <div
      data-testid='budget-comparison-bar'
      className={clsx('flex flex-col', {
        'gap-2': !condensed,
        'gap-1': condensed,
      })}
    >
      <Bar
        label={beforeLabel}
        isAfter={false}
        amount={beforeAmount}
        maxAmountSubunits={xAxisMaxAmountSubunits}
        condensed={condensed}
        increaseIsGreen={increaseIsGreen}
        percentageChange={percentageChange}
        useMinLabelWidth={useMinLabelWidth}
      />
      <Bar
        label={afterLabel}
        isAfter
        amount={afterAmount || beforeAmount}
        maxAmountSubunits={xAxisMaxAmountSubunits}
        condensed={condensed}
        increaseIsGreen={increaseIsGreen}
        percentageChange={percentageChange}
        amountIsUnknown={!afterAmount}
        useMinLabelWidth={useMinLabelWidth}
      />
    </div>
  )
}

export default BudgetComparisonBar
